import { memo, useState } from "react";
import { toast } from "react-toastify";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useOrganizationStore } from "stores/OrganizationStore";
import { useUserStore } from "stores/UserStore";

import { requestSalesforceQuoteSynchronous } from "model/salesforce";

import { toCurrency } from "utility/misc";
import { FOTS, SUTS } from "utility/typescript";

// -----------------------------------------------------------------------------

const BillingManagePlanManagePlanCurrentFeatures = memo(
  function BillingManagePlanManagePlanCurrentFeatures({
    label,
    interval,
    features,
    isTrial,
    price = 0,
    daysLeft = 0,
  }: BillingManagePlanManagePlanCurrentFeaturesProps): JSX.Element {
    const [userStore] = useUserStore();
    const [organizationStore] = useOrganizationStore();

    const [showModal, setShowModal] = useState(false);
    const [prefillForm, setPrefillForm] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [website, setWebsite] = useState("");

    //
    function openModal() {
      setShowModal(true);
    }

    function closeModal() {
      setShowModal(false);
      setPrefillForm(false);
    }

    function togglePrefillForm(newPrefillForm: boolean) {
      setPrefillForm(newPrefillForm);

      if (newPrefillForm) {
        setName(userStore.user?.fullName);
        setEmail(userStore.user?.email);
        setCompanyName(organizationStore.organization.name);

        if (organizationStore.organization.domain) {
          // pull domain from user's email address
          setWebsite(
            userStore.user?.email
              .substring(
                userStore.user?.email.indexOf("@") + 1,
                userStore.user?.email.length
              )
              .trim()
          );
        }
      } else {
        setName("");
        setEmail("");
        setCompanyName("");
        setWebsite("");
      }
    }

    function requestQuote() {
      setShowModal(false);
      requestSalesforceQuoteSynchronous(
        {
          request_full_name: name,
          request_email: email,
          request_company_name: companyName,
          request_domain: website,
        },
        () => toast.success("Your request has been submitted!"),
        () => toast.error("There was an error submitting your request")
      );
    }

    //
    return (
      <Paper sx={PaperSectionSX}>
        <Stack direction="row" sx={SectionHeaderSX}>
          <Typography variant="h6" fontWeight="bold" sx={SectionHeaderLabelSX}>
            {label}
          </Typography>
          <Typography variant="h6">
            <Box component="strong">{toCurrency(price)}</Box>
            <Box component="span" sx={PriceIntervalSX}>
              /{interval}
            </Box>
          </Typography>
        </Stack>
        <Dialog open={showModal} onClose={closeModal}>
          <DialogTitle>Upgrade Your ThreatKey Plan</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To upgrade your ThreatKey plan, fill out the form below and one of
              our representatives will be in touch with you shortly. If you have
              any questions, please contact us via the chat icon in the bottom
              right corner.
            </DialogContentText>
            <FormGroup>
              <FormControlLabel
                label="Use my information to pre-fill the form"
                control={
                  <Checkbox
                    checked={prefillForm}
                    onChange={(event: FOTS) =>
                      togglePrefillForm(event?.target?.checked)
                    }
                  />
                }
              />
            </FormGroup>
            <TextField
              autoFocus
              margin="dense"
              id="email"
              label="Contact Email Address"
              type="email"
              fullWidth
              variant="standard"
              value={email}
              onChange={
                ((e: SUTS<{ target: { value: string } }>) =>
                  setEmail(e?.target?.value)) as FOTS
              }
            />
            <TextField
              margin="dense"
              id="name"
              label="Contact Name"
              type="text"
              fullWidth
              variant="standard"
              value={name}
              onChange={
                ((e: SUTS<{ target: { value: string } }>) =>
                  setName(e?.target?.value)) as FOTS
              }
            />
            <TextField
              margin="dense"
              id="company_name"
              label="Company Name"
              type="text"
              fullWidth
              variant="standard"
              value={companyName}
              onChange={
                ((e: SUTS<{ target: { value: string } }>) =>
                  setCompanyName(e?.target?.value)) as FOTS
              }
            />
            <TextField
              margin="dense"
              id="company_website"
              label="Company Website"
              type="text"
              fullWidth
              variant="standard"
              value={website}
              onChange={
                ((e: SUTS<{ target: { value: string } }>) =>
                  setWebsite(e?.target?.value)) as FOTS
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal} color="error" variant="contained">
              Cancel
            </Button>
            <Button onClick={requestQuote} variant="contained">
              Request Quote
            </Button>
          </DialogActions>
        </Dialog>

        {isTrial ? (
          <Paper sx={TrialLabelContainerSX}>
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlinedIcon color="info" />
              <Typography sx={TrialLabelWarningSX} color="primary">
                You have <strong>{daysLeft} days</strong> left on your
                fully-featured free trial.
              </Typography>
              <Button variant="text" color="info" onClick={openModal}>
                Upgrade Now
              </Button>
            </Stack>
          </Paper>
        ) : null}

        <Stack spacing={0.25} p={2}>
          {features?.map((feature: string) => (
            <Typography variant="h6" sx={FeatureLabelSX} key={feature}>
              <CheckIcon sx={FeatureIconSX} />
              {feature}
            </Typography>
          )) ?? null}
        </Stack>
      </Paper>
    );
  }
);

export default BillingManagePlanManagePlanCurrentFeatures;

// -----------------------------------------------------------------------------

const PaperSectionSX = { p: 1.5 };

const SectionHeaderSX = { borderBottom: "1px rgba(0, 0, 0, 0.25) solid" };

const SectionHeaderLabelSX = { flexGrow: 1 };

const PriceIntervalSX = { opacity: 0.6, ml: 0.5 };

const TrialLabelContainerSX = {
  m: 2,
  mb: 0,
  pl: 2,
  pr: 2,
  borderColor: (theme: Theme) => `${theme.palette.info.main} !important`,
};

const TrialLabelWarningSX = { flexGrow: 1 };

const FeatureLabelSX = { display: "flex", alignItems: "center" };

const FeatureIconSX = { mr: 2 };

// -----------------------------------------------------------------------------

interface BillingManagePlanManagePlanCurrentFeaturesProps {
  label: string;
  interval: string;
  features: string[];
  isTrial: boolean;
  price?: number;
  daysLeft?: number;
}
